import { CHECKER_NORMAL_16 } from "~/../node_modules/@sznds/icons/index";
import { IEventsListResponse } from "~/activerequest/interfaces";
import createSDSIcon from "~/ui/icon";
import { createReviewLicence } from "~/ui/review/licence";
import { node } from "~/util/dom";
import { fetchActiveRequests, updateReviewState, putConfirm } from "~/util/providers/review";
import { createTimeHint } from "~/util/review";
import Subpage from "./subpage";
import SuggestedItem from "./suggesteditem";
import { addListener, removeListeners } from "~/lib/signals";
import SuggestedUpdateItem from "./suggestedupdateitem";
import { getUserSettings } from "~/util/providers/mapybox";
import { ESETTINGS_KEYS } from "~/state/user/interfaces";
import { getSignal, sendAnalytics } from "~/activerequest/util";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { SUGGESTRED_REVIEWS_PER_PAGE, SCROLL_TRESHOLD } from "~/mymaps2/reviews/constants";
import { useScroll } from "~/util/usescroll";
import { getIcon } from "~/util/providers/poiagg";
import { setCoordinates } from "~/state/map/state";
import { store } from "~/state/store";

interface ISuggestedReviewsOptions {
	tabs: HTMLUListElement;
}

export default class SuggestedReviews extends Subpage {
	_tabs: HTMLUListElement;

	_list: HTMLUListElement;

	_topBar: HTMLDivElement;

	_loading: boolean;

	_intro: HTMLDivElement;

	_layer: any;

	_sc: Array<any>;

	_offset: number;

	_resultCount: number;

	_lastHint: string;

	_scrollListener: ReturnType<typeof useScroll>;

	constructor({ tabs }: ISuggestedReviewsOptions) {
		super();

		this._loading = false;
		this._offset = 0;
		this._resultCount = 0;
		this._lastHint = "";
		this._sc = [];
		this._tabs = tabs;
		this._container.classList.add("suggestedreviews");
		this._list = node("ul", { className: "suggestedreviews__list" });
		this._topBar = node("div", { className: "myreviews__topbar" });
		this._intro = node("div", { className: "suggestedreviews__intro", innerHTML: `<p>${_("mymap.reviews.suggestIntro")}</p>` });
		this._intro.appendChild(createReviewLicence(true, type => {
			Mapy.getComponent("logger").log("click", {
				text: type === "agr" ? "podmínkami" : "pravidly",
				section: "suggestionDashboard",
			});
		}));
		this._layer = Scene.createLayerMarker();
		this._scrollListener = null;
	}

	activate() {
		super.activate();

		if (this._active) {
			return;
		}

		this._active = true;
		this._offset = 0;
		this._resultCount = 0;
		this._lastHint = "";
		this._scrollListener = useScroll({
			threshold: SCROLL_TRESHOLD,
			onScroll: (isBottom: boolean) => {
				const reviewsPerPage = SUGGESTRED_REVIEWS_PER_PAGE;

				if (
					!this._loading
					&& isBottom
					&& this._resultCount > 0
					&& this._offset + reviewsPerPage < this._resultCount
				) {
					this._offset += reviewsPerPage;

					this._loadSuggestions();
				}
			},
		});

		this._sc = [
			addListener(this, "detail-activated", "_detailActivated"),
			addListener(this, "detail-deactivated", "_detailDeactivated"),
		];
		this._clear();
		this._topBar.appendChild(this._tabs);
		this._loadSuggestions();
		Scene.addLayer(this._layer).enable();
	}

	deactivate() {
		super.deactivate();

		if (!this._active) {
			return;
		}

		this._active = false;
		this._scrollListener && this._scrollListener();
		this._scrollListener = null;

		removeListeners(this._sc);
		this._layer.removeAll();
		this._layer.disable();
		Scene.removeLayer(this._layer);
		Scene.clearAllWasmContent();
	}

	_clear() {
		this._container.innerHTML = "";
		this._list.innerHTML = "";
		this._container.appendChild(this._topBar);
		this._layer.removeAll();
	}

	async _loadSuggestions() {
		if (this._loading) {
			return;
		}

		this._loading = true;
		Mapy.getComponent("loader").start();
		// FIXME: CSS trida je cizi
		const loader = node("img", { src: "/img/loader.gif", width: 24, height: 24, className: "myreviews__loader", crossOrigin: "anonymous" });

		this._container.appendChild(loader);

		// nasosame data
		try {
			const response = await fetchActiveRequests([
				`offset=${this._offset}`,
				`limit=${SUGGESTRED_REVIEWS_PER_PAGE}`,
			]);
			const result = await response.json() as IEventsListResponse;

			this._resultCount = result.total;
			putConfirm();

			if (!result.events?.length) {
				throw new Error("Nic nemame v navrzich na hodnoceni.");
			}

			// na vsechny souradnice budeme chtit zazoomovat
			const coordsArray = [];

			if (this._offset === 0) {
				this._list.innerHTML = "";
				this._lastHint = "";
			}

			result.events.forEach(event => {
				const hint = createTimeHint(CONFIG.defaultLang, event.time);

				// pokud dostavame jiny casovy hint nez posledne, udelame mu polozku do vypisu
				if (hint !== this._lastHint) {
					this._list.appendChild(node("li", { className: "suggestedreviews__hint", innerHTML: hint }));
					this._lastHint = hint;
				}

				// jde o zadost o update
				if (event.review) {
					// polozka do vypisu
					const item = new SuggestedUpdateItem({
						event,
						onReject: async () => {
							Mapy.getComponent("logger").log("suggestion_update_close", {
								section: "suggestionDashboard",
								signal: getSignal(event),
							});
							await updateReviewState(event.id, "never_this_poi");

							this._clear();
							this._loadSuggestions();
						},
						onFinished: async closeData => {
							if (closeData.wasRemoved) {
								await updateReviewState(event.id, "ignore");
							} else {
								await updateReviewState(event.id, closeData?.myReview?.text ? "rated_review" : "rated");
							}

							this._clear();
							this._loadSuggestions();
						},
					});

					this._list.appendChild(item.node);
				} else {
					// polozka do vypisu
					const item = new SuggestedItem({
						event,
						onWrongPOI: async () => {
							await updateReviewState(event.id, "wrong_poi");

							this._clear();
							this._loadSuggestions();
						},
						onReject: async () => {
							// smazani poie pres popelnici
							sendAnalytics("suggestion_flow_close", event, {
								count: 0,
								origin: "closeSuggestion",
								typePoi: item.isEventCorrected ? "corrected" : "suggested",
							});
							await updateReviewState(event.id, "never_this_poi");

							this._clear();
							this._loadSuggestions();
						},
						onFinished: async closeData => {
							// ohodnoceni poie
							sendAnalytics("suggestion_flow_close", event, {
								count: closeData.wasRemoved || !closeData.myReview ? 0 : 1,
								origin: closeData.wasRemoved ? "deleteReview" : "noLine",
								typePoi: item.isEventCorrected ? "corrected" : "suggested",
							});
							if (closeData.wasRemoved) {
								await updateReviewState(event.id, "ignore");
							} else {
								await updateReviewState(event.id, closeData?.myReview?.text ? "rated_review" : "rated");
							}

							this._clear();
							this._loadSuggestions();
						},
					});

					this._list.appendChild(item.node);
				}

				// marker do mapy
				const { typeId, mark, source, id } = event.data.poi.poiagg;
				const icon = getIcon(typeId, { scale: 1 });
				const icon2x = getIcon(typeId, { scale: 2 });
				const coords = Scene.coordsFromWGS84(mark.lon, mark.lat);
				const opts = {
					type: icon ? Scene.VISUAL.MARKER_WINDY.SMALL : Scene.VISUAL.MARKER_WINDY.UNIVERSAL,
					...icon ? { icon, srcset: `${icon} 1x${icon2x ? `, ${icon2x} 2x` : ""}` } : {},
				};
				const marker = Scene.createMarkerMapyWindy(coords, undefined, opts);

				marker.addEventListener("click", () => {
					Mapy.getComponent("detail").setState({
						source,
						id,
						backTitle: _("mymap.reviews.tabSuggested"),
					}).then(() => {
						Mapy.getComponent("history").save();
					}, () => {});
				});

				this._layer.addMarker(marker);
				coordsArray.push(coords);
			});

			// nastavime mapu, aby vsechno bylo videt najednou
			const cz = Scene.computeCenterZoom(coordsArray, true);
			const [longitude, latitude] = cz[0].toWGS84();

			store.dispatch(setCoordinates({
				longitude,
				latitude,
				zoom: cz[1],
				animate: true,
			}));

			this._container.appendChild(this._intro);
			this._container.appendChild(this._list);
		// neco se pokazilo, musime ukazat prazdnou stranku.
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.log(ex);

			try {
				const settings = await getUserSettings();
				const item = settings?.items?.filter(actualItem => actualItem.title === ESETTINGS_KEYS.REVIEW_NOTIFICATIONS_ENABLED)[0];

				if (item?.data?.value === false) {
					const settingsButton = node("button", { className: "suggestedreviews__settingsButton", type: "button", innerHTML: _("mymap.reviews.turnSettingsOn") });

					this._container.appendChild(node("p", { className: "suggestedreviews__emptyText", textContent: _("mymap.reviews.emptySuggestedOff") }));
					settingsButton.setAttribute("data-log-ignore", "true");
					settingsButton.addEventListener("click", () => {
						Mapy.getComponent("logger").log("suggestion_reviews_on", {
							section: "suggestionDashboard",
						});
						Mapy.getComponent("settings").activate();
					});
					settingsButton.prepend(createSDSIcon({ symbol: CHECKER_NORMAL_16 }));
					this._container.appendChild(settingsButton);

					this._container.appendChild(node("img", { className: "suggestedreviews__emptyImg", src: "/img/mymap/empty-suggestedoffreviews.svg" }));
				} else {
					throw new Error();
				}
			} catch (exception) {
				// eslint-disable-next-line no-console
				console.log(exception);
				this._container.appendChild(node("p", { className: "suggestedreviews__emptyText", textContent: _("mymap.reviews.emptySuggestedOn") }));
				this._container.appendChild(node("img", { className: "suggestedreviews__emptyImg", src: "/img/mymap/empty-suggestedonreviews.svg" }));
			}
		} finally {
			Mapy.getComponent("loader").end();
			loader.remove();
			this._loading = false;
		}
	}

	_detailActivated() {
		this._layer.disable();
	}

	_detailDeactivated() {
		this._layer.enable();
	}
}
